import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ExternalLink, LogOut, Paperclip, Phone, Send } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import {
	type AmbientChip,
	AmbientGreeting,
	ConversationStarters,
} from '@/components/ambient-greeting';
import { CRISIS_CARD_HEADLINE, CrisisResources } from '@/components/crisis-resources';
import { FileUpload, FileUploadContent, FileUploadTrigger } from '@/components/file-upload';
import { InviteGate } from '@/components/invite-gate';
import { Loader } from '@/components/loader';
import { MessageBubble } from '@/components/message-bubble';
import {
	PromptInput,
	PromptInputAction,
	PromptInputActions,
	PromptInputTextarea,
} from '@/components/prompt-input';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Bubble, BubbleContent } from '@/components/ui/bubble';
import { Button, buttonVariants } from '@/components/ui/button';
import { Marker, MarkerContent } from '@/components/ui/marker';
import { Message, MessageContent } from '@/components/ui/message';
import {
	MessageScroller,
	MessageScrollerButton,
	MessageScrollerContent,
	MessageScrollerItem,
	MessageScrollerProvider,
	MessageScrollerViewport,
} from '@/components/ui/message-scroller';
import { useBotChallenge } from '@/hooks/use-bot-challenge';
import { useChat } from '@/hooks/use-chat';
import { useEmbedBridge } from '@/hooks/use-embed-bridge';
import {
	chatInfoStorageKey,
	probeEndpointStatusWithTimeout,
	useEndpointStatus,
} from '@/hooks/use-endpoint-status';
import { useInviteGate } from '@/hooks/use-invite-gate';
import { resolveAssetUrl } from '@/lib/asset-url';
import { ATTACHMENT_ACCEPT, filesFromClipboard } from '@/lib/attachments';
import { AI_ENABLED } from '@/lib/build-flags';
import { bothChatModesAvailable, isAiOnlyDistrict, resolveTransportMode } from '@/lib/chat-mode';
import { prefetchConnectChatJS } from '@/lib/connect-chatjs';
import { useEmbedContext } from '@/lib/embed-context';
import { fullPageUrlFor } from '@/lib/full-page-url';
import { HISTORY_DIVIDER_ID, toHandoffHistory } from '@/lib/handoff-history';
import { getTransportConfig } from '@/lib/runtime-env';
import { CHAT, STATE, testId } from '@/lib/selectors';
import { setConversationInProgress } from '@/lib/sw-update';
import { cn } from '@/lib/utils';
import type { ChatMessage, TransportConfig, TransportMode } from '@/types';

/**
 * Shown until the district's own Greeting__c arrives from endpoint-status.
 *
 * chat-handler keeps a byte-identical copy (`DEFAULT_GREETING_TEXT` in
 * `cdk/lambda/chat-handler/handlers/session/shared.ts`) for the greeting it
 * records on the session. Separate build units, so no shared import is
 * possible — the two have to be changed together.
 */
export const DEFAULT_AI_GREETING =
	"Hey! I'm NoMi. I'm trained to listen and help. Let's talk. What can I call you?";

export const DEFAULT_COUNSELOR_WELCOME =
	'Welcome! Send a message and you’ll be connected with a counselor.';

const chatSearchSchema = z.object({
	// Optional so beforeLoad can distinguish an explicit request (picker choice
	// or partner-page hint) from "nothing asked" — the resolution rules differ
	// (see resolveTransportMode). beforeLoad always redirects to a definite
	// mode when a district is known.
	mode: z.enum(['reachout-ai', 'classic-chat']).optional().catch(undefined),
	// Forwarded from the home redirect (originally on the partner-page iframe
	// URL). Semicolon-delimited Endpoint Features__c — used to activate
	// BotChallenge without a runtime probe.
	features: z.string().optional(),
	// `d` and `embed` are read by useEmbedBridge from window.location.search;
	// listing them in the schema keeps TanStack Router from stripping them on
	// navigation back to /chat from elsewhere in the app.
	d: z.string().optional(),
	embed: z.enum(['floating', 'inline', 'ambient']).optional(),
});

export const Route = createFileRoute('/chat')({
	validateSearch: chatSearchSchema,
	// Server-authoritative mode routing. The endpoint-status probe (cached,
	// raced against a short timeout so a slow Lambda never stalls the student)
	// decides AI vs Connect; the URL `?mode=`/`?features=` are only provisional
	// hints. This is what lets us flip a district between modes from Salesforce
	// without school admins touching the embed tag in their CMS.
	beforeLoad: async ({ search }) => {
		const base = getTransportConfig();
		const districtId = search.d ?? base.districtId;
		const hasConnectConfig = !!(base.connectApiEndpoint && base.connectCode);

		const status = districtId
			? await probeEndpointStatusWithTimeout(districtId, 1500)
			: ({ state: 'unknown' } as const);

		const resolved = resolveTransportMode({
			requested: search.mode,
			urlFeatures: search.features,
			status,
			hasConnectConfig,
		});
		if (resolved !== search.mode) {
			throw redirect({
				to: '/chat',
				search: { ...search, mode: resolved },
				replace: true,
			});
		}
	},
	component: ChatPage,
});

function ChatPage() {
	const { mode: modeParam, features: urlFeaturesRaw, d: districtParam, embed } = Route.useSearch();
	// beforeLoad redirects to a definite mode whenever a district is known;
	// the fallback only applies to bare dev previews.
	const mode = modeParam ?? 'reachout-ai';

	// Ambient embeds auto-play a locally simulated greeting (zero backend
	// traffic) and only connect once the student actually interacts.
	const isAmbient = embed === 'ambient';
	const [hasInteracted, setHasInteracted] = useState(false);
	// Bumped to re-run the lazy-connect effect in place — the only way to seed a
	// fresh session for a district that has no picker to navigate back to (see
	// handleBack).
	const [sessionEpoch, setSessionEpoch] = useState(0);
	const navigate = useNavigate();
	const [input, setInput] = useState('');
	const { sendMessageSent, sendSessionEnded, districtUuid, embedded } = useEmbedBridge();

	// URL features are the partner-page generator's authoritative copy of
	// Endpoint.Features__c at generation time — same source the auto-route
	// used. Treated as canonical for feature-gated UX so we don't need a
	// runtime probe for BotChallenge to activate.
	const urlFeatures = useMemo<string[]>(
		() =>
			(urlFeaturesRaw ?? '')
				.split(';')
				.map((f) => f.trim())
				.filter(Boolean),
		[urlFeaturesRaw],
	);

	const baseConfig = useMemo(() => getTransportConfig(), []);

	// Where this chat is mounted, and therefore who owns the DOM around it. In an
	// in-page element the school owns `window`, `document.body` and the URL, so
	// the bus, portals and reload path all come from here instead.
	const embedContext = useEmbedContext();
	const interactiveBus = embedContext.bus;

	// Kill-switch probe. Runs once per session per district. Static maintenance
	// page baked at generation time is the primary defense \u2014 this catches the
	// case where a school's CDN serves a stale live page after a district is
	// disabled.
	const districtId = districtUuid ?? baseConfig.districtId ?? null;
	const endpointStatus = useEndpointStatus(districtId || null);
	const isDistrictDisabled = endpointStatus.state === 'disabled';

	// The picker only exists for districts entitled to both surfaces, so the
	// header's back arrow only exists there too — a single-surface district must
	// have no route back to a page offering the mode it doesn't have. The
	// picker's own beforeLoad enforces the same rule, so a stale history entry
	// or a hand-typed `/` bounces straight back to /chat.
	const canReturnToPicker = bothChatModesAvailable({
		urlFeatures: urlFeaturesRaw,
		status: endpointStatus,
	});

	// Bot challenge gate. Only districts with the `BotChallenge` Feature
	// require a CAPTCHA before chat starts. The hook is a no-op for everyone
	// else. Kept entirely client-side here; backend enforcement of the WAF
	// token is a follow-up.
	//
	// Source of truth: prefer URL features (the partner page just baked them
	// in) over endpointStatus.features (which depends on a runtime probe to
	// chat-handler over an HTTP entry point that isn't always reachable).
	// Either match activates the gate.
	// AI_ENABLED first: the WebACL this puzzle satisfies sits in front of the
	// AppSync Events API, which a Connect-only build never speaks to. (It is also
	// inert for in-page mounts on any build — the WAF token domains and CAPTCHA
	// API key are bound to our own zone, not a school's.)
	const botChallengeEnabled =
		AI_ENABLED &&
		(urlFeatures.includes('BotChallenge') ||
			(endpointStatus.state === 'enabled' && endpointStatus.features.includes('BotChallenge')));
	const botChallenge = useBotChallenge({
		enabled: botChallengeEnabled,
		wafIntegrationUrl: baseConfig.wafIntegrationUrl,
		wafCaptchaApiKey: baseConfig.wafCaptchaApiKey,
	});
	const isBotChallengeBlocking =
		botChallenge.state === 'pending' || botChallenge.state === 'failed';

	// Invite gate. Three signals can activate it:
	//   1. URL `features` contains a synthetic `InviteRequired` flag baked at
	//      partner-page generation time (mirrors how BotChallenge is plumbed).
	//   2. The `endpoint-status` probe returns `inviteRequired: true` —
	//      direct-visit fallback when the partner page is bypassed.
	//   3. The backend publishes an authFailure of `invalid_invite_code` in
	//      response to a join-session — latches the gate on regardless of
	//      upstream signals. Makes the frontend resilient to partial
	//      deployments and matches the user expectation that an "Invalid
	//      invite code" error always re-prompts for the code.
	// Server-side validation in chat-handler is authoritative; these are
	// signals to the UI about *whether* to render the gate.
	const [authFailureLatched, setAuthFailureLatched] = useState(false);
	// AI_ENABLED first: the invite code only travels on an AppSync join
	// (lib/appsync-transport.ts), so in a Connect-only build the gate would ask a
	// student for a code that nothing ever checks. Folding it to false here also
	// takes `gated`, the gate's JSX and <InviteGate> itself out of that bundle.
	const inviteGateEnabled =
		AI_ENABLED &&
		(urlFeatures.includes('InviteRequired') ||
			(endpointStatus.state === 'enabled' && endpointStatus.inviteRequired) ||
			authFailureLatched);

	// The merged transport config carries the gate-supplied invite code through
	// to `useChat`. `useChat` reads `configRef.current` only at connect time, so
	// any update before the first send (lazy connect) or before the gate calls
	// `connectWithMode` (eager verify) is picked up.
	const [gateInviteCode, setGateInviteCode] = useState<string | undefined>(undefined);
	// Merge the URL-derived districtUuid into the transport config so
	// `AppSyncTransport.publishJoinSession` sends `districtId` on every
	// join. Without this, `useChat` only sees `baseConfig.districtId` from
	// the runtime-env (`DISTRICT_ID`), which is unset for the Universal
	// embed — so the chat-handler creates anonymous sessions even though
	// the partner page passed `?d=<Code__c>` on the iframe URL.
	//
	// The same `?d=<Code__c>` is also the district's contact code for
	// Amazon Connect (classic-chat). The reachout-contact-center page
	// generator passes `endpoint_record["Code__c"]` as `?d=`, and the legacy
	// AC widget snippet used that same value as `code:` in
	// `AmazonCustomChatWidget.ChatInterface.init`. So when the URL provides
	// it, override `connectCode` too — the runtime-config value (CONNECT_CODE
	// / NEXT_PUBLIC_CONNECT_CODE) is only a fallback for direct visits where
	// no district is known.
	const config = useMemo(() => {
		const merged: TransportConfig = gateInviteCode
			? { ...baseConfig, inviteCode: gateInviteCode }
			: { ...baseConfig };
		if (districtUuid) {
			merged.districtId = districtUuid;
			merged.connectCode = districtUuid;
		}
		if (isAmbient) {
			// The ambient greeting is already on screen — don't have the backend
			// publish it again on join (it's still recorded server-side).
			merged.suppressGreeting = true;
		}
		return merged;
	}, [baseConfig, gateInviteCode, districtUuid, isAmbient]);

	// Counselor handover availability. Must derive from the merged `config`
	// (NOT `baseConfig`) so that a Universal-page embed — where runtime-config
	// carries no CONNECT_CODE because it's a per-district value supplied via
	// `?d=<Code__c>` on the iframe URL — still shows the "Talk to a Counselor"
	// button in AI mode. Reading `baseConfig.connectCode` here would mean the
	// button is hidden whenever the connect code only exists in URL state,
	// which is exactly the Universal AI flow.
	//
	// Invariant: if a session can succeed in classic-chat (the same URL +
	// env produce a non-empty `config.connectCode` and `config.connectApiEndpoint`),
	// the AI-mode handover button must be visible. Both this gate and the
	// actual handover request go through `config`, so they can't disagree.
	const canSwitchToCounselor = !!(config.connectApiEndpoint && config.connectCode);

	// Chatbot-only districts don't get the counselor as a *choice*: no ambient
	// chip, no "Talk to a Counselor" button. The safety ladder is unaffected —
	// `canSwitchToCounselor` still gates the fallback offer shown when the AI
	// path fails, and the `feature_not_enabled` reroute below still works.
	// Deliberately fails open: only a positive AI-only signal hides these.
	const canChooseCounselor =
		canSwitchToCounselor &&
		!isAiOnlyDistrict({ urlFeatures: urlFeaturesRaw, status: endpointStatus });

	const {
		messages,
		status,
		isRemoteTyping,
		remoteTypingName,
		streamingContent,
		streamingMessageId,
		error,
		attachmentError,
		activeMode,
		connectWithMode,
		setMode,
		addLocalMessage,
		sendMessage,
		sendTyping,
		requestHumanAssistance,
		endChat,
		sendAttachment,
		getAttachmentUrl,
		dismissAttachmentError,
		disconnect,
		handoverStatus,
	} = useChat({ config });

	const [isEndChatDialogOpen, setIsEndChatDialogOpen] = useState(false);
	const [isEndingChat, setIsEndingChat] = useState(false);
	const [endChatFailed, setEndChatFailed] = useState(false);

	// Latest auth-failure code from the system message stream. The backend
	// publishes these via `mapJoinSessionAuthFailure` when join-session is
	// rejected (e.g. invalid invite code, district disabled).
	const lastAuthFailureCode = useMemo(() => {
		for (let i = messages.length - 1; i >= 0; i--) {
			const m = messages[i];
			if (m.authFailure && m.authFailureCode) return m.authFailureCode;
		}
		return null;
	}, [messages]);

	// The backend refused this page outright: the district is switched off, or
	// an in-page chat is running on a site outside the district's allowlist.
	// Neither is something the student can fix from the composer, so the chat
	// shows the same closed state the endpoint-status probe would — an in-page
	// element cannot probe, and only learns this when the join is rejected.
	const isOriginBlocked = lastAuthFailureCode === 'origin_not_allowed';
	const isAccessBlocked = isOriginBlocked || lastAuthFailureCode === 'district_disabled';

	const invite = useInviteGate({
		enabled: inviteGateEnabled,
		apiEndpoint: baseConfig.apiEndpoint,
		districtId: districtId ?? undefined,
		authFailureCode: lastAuthFailureCode,
		isConnected: status === 'connected',
		botChallengeReady: !isBotChallengeBlocking,
		requestVerify: () => {
			void connectWithMode('reachout-ai');
		},
	});

	// Mirror gate state into top-level state so the next render's config memo
	// includes the code before useChat's configRef snapshot is taken.
	useEffect(() => {
		setGateInviteCode(invite.inviteCode);
	}, [invite.inviteCode]);

	// Latch the gate on when the backend rejects with invalid_invite_code, even
	// if no upstream signal said the gate was needed. Without this, a partial
	// deployment (chat-handler enforcing invite + frontend rendered, but partner
	// page not yet regenerated and endpoint-status probe not yet redeployed)
	// would let the user past the gate, see the system error, and never get a
	// chance to enter a code.
	useEffect(() => {
		if (lastAuthFailureCode === 'invalid_invite_code' && !authFailureLatched) {
			setAuthFailureLatched(true);
		}
	}, [lastAuthFailureCode, authFailureLatched]);

	// Per-district greeting (Greeting__c via endpoint-status). The backend
	// builds the session greeting from the same field, so the local welcome
	// bubble and the recorded message stay in sync. Read through a ref so a
	// late-resolving probe can't re-trigger the connect effect (which would
	// disconnect and wipe an in-flight conversation) — if the probe loses the
	// race, the reducer's welcome-swap corrects the text on join instead.
	const districtGreeting = endpointStatus.state === 'enabled' ? endpointStatus.greeting : undefined;
	const districtGreetingRef = useRef(districtGreeting);
	districtGreetingRef.current = districtGreeting ?? districtGreetingRef.current;

	// Rung 3 of the fallback ladder: hop to the classic transport. Navigating
	// the search param drives the existing mode-change machinery (disconnect →
	// fresh welcome); the typed-but-unsent input survives because the component
	// doesn't unmount, and the conversation itself is snapshotted here so the
	// lazy-connect effect can re-seed it as history after the RESET.
	const handoffHistoryRef = useRef<ChatMessage[]>([]);
	const switchToDirectConnect = useCallback(() => {
		handoffHistoryRef.current = toHandoffHistory(messages);
		navigate({
			to: '/chat',
			search: (prev) => ({ ...prev, mode: 'classic-chat' as const }),
			replace: true,
		});
	}, [messages, navigate]);

	// Backend backstop fired: this district is Connect-only but the client
	// attempted an AI join (hand-edited URL or a stale embed that beat the
	// endpoint-status probe). Reroute to counselor chat instead of stranding
	// the student on an error.
	useEffect(() => {
		if (
			lastAuthFailureCode === 'feature_not_enabled' &&
			canSwitchToCounselor &&
			mode === 'reachout-ai'
		) {
			switchToDirectConnect();
		}
	}, [lastAuthFailureCode, canSwitchToCounselor, mode, switchToDirectConnect]);

	// Set up lazy connect for both modes. Previously reachout-ai connected on
	// mount (which created an empty session in DDB for every student who
	// landed on the page but never typed). Now the widget renders a static
	// welcome prompt and only creates the session on the first user message,
	// mirroring the classic-chat pattern.
	//
	// biome-ignore lint/correctness/useExhaustiveDependencies: `sessionEpoch` is a deliberate re-run trigger, not a value the body reads — bumping it is how a restart-in-place tears the ended session down (cleanup) and seeds a fresh one (body).
	useEffect(() => {
		if (isDistrictDisabled || isAccessBlocked) return;
		if (isBotChallengeBlocking) return;
		if (inviteGateEnabled && !invite.unlocked) return;
		const m = mode as TransportMode;
		if (m === 'reachout-ai') {
			setMode('reachout-ai');
			// Ambient embeds render the greeting via <AmbientGreeting> (animated,
			// visibility-gated) instead of a static welcome bubble.
			if (!isAmbient) {
				addLocalMessage({
					id: 'welcome',
					role: 'assistant',
					content: districtGreetingRef.current ?? DEFAULT_AI_GREETING,
					timestamp: new Date().toISOString(),
				});
			}
		} else {
			setMode('classic-chat');
			// Rung-3 hop: replay the AI conversation the RESET just wiped, as
			// inert history above the classic welcome. The ref is intentionally
			// not cleared here \u2014 StrictMode runs body\u2192cleanup(RESET)\u2192body, and the
			// second run must re-seed; stable `history-` ids make it an upsert.
			const history = handoffHistoryRef.current;
			if (history.length > 0) {
				addLocalMessage({
					id: HISTORY_DIVIDER_ID,
					role: 'system',
					systemEvent: true,
					content: 'Earlier conversation with Nomi',
					timestamp: history[0].timestamp,
				});
				for (const m of history) {
					addLocalMessage(m);
				}
			}
			if (!isAmbient) {
				addLocalMessage({
					id: 'welcome',
					role: 'system',
					content: DEFAULT_COUNSELOR_WELCOME,
					timestamp: new Date().toISOString(),
				});
			}
		}
		return () => {
			disconnect();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		mode,
		setMode,
		addLocalMessage,
		disconnect,
		isDistrictDisabled,
		isAccessBlocked,
		isBotChallengeBlocking,
		inviteGateEnabled,
		invite.unlocked,
		isAmbient,
		// Restart-in-place: cleanup disconnects the ended session, then the body
		// re-seeds mode + welcome so the student can type again.
		sessionEpoch,
	]);

	// Focus management across the gate -> chat swap.
	//
	// The bot challenge, the invite gate and the restart-in-place all replace the
	// entire subtree. Focus stayed on the detached node, so a keyboard or screen
	// reader user was dropped at the top of the document with no announcement
	// that the chat had opened. Move them to the composer, which is both the
	// landmark and the thing they came here to use.
	const composerRef = useRef<HTMLTextAreaElement>(null);
	const gated =
		isDistrictDisabled ||
		isAccessBlocked ||
		isBotChallengeBlocking ||
		(inviteGateEnabled && !invite.unlocked);
	const wasGatedRef = useRef(gated);
	useEffect(() => {
		const opened = wasGatedRef.current && !gated;
		wasGatedRef.current = gated;
		// Ambient embeds live on a school homepage; stealing focus there would
		// scroll the page out from under someone who never asked for the chat.
		if (opened && !isAmbient) composerRef.current?.focus();
	}, [gated, isAmbient]);

	const sendMessageRef = useRef(sendMessage);
	useEffect(() => {
		sendMessageRef.current = sendMessage;
	}, [sendMessage]);

	useEffect(() => {
		const handler = (e: Event) => {
			const detail = (e as CustomEvent).detail;
			if (detail?.type === 'auto_send' && detail.text) {
				sendMessageRef.current(detail.text);
			} else if (detail?.type === 'send_message' && detail.text) {
				setInput(detail.text);
			}
		};
		// Scoped to this chat instance rather than `window`: two elements on one
		// school page would otherwise both act on either one's card taps.
		interactiveBus.addEventListener('interactive-action', handler);
		return () => interactiveBus.removeEventListener('interactive-action', handler);
	}, [interactiveBus]);

	const handleSend = useCallback(() => {
		const text = input.trim();
		if (!text) return;
		const isLazyReady =
			(activeMode === 'reachout-ai' || activeMode === 'classic-chat') && status === 'disconnected';
		if (status !== 'connected' && !isLazyReady) return;
		sendMessage(text);
		setInput('');
		sendMessageSent();
	}, [input, status, activeMode, sendMessage, sendMessageSent]);

	const handleBack = useCallback(() => {
		handoffHistoryRef.current = [];
		sendSessionEnded('user-closed');

		// Single-surface district: `/` has no picker to show, so its beforeLoad
		// redirects straight back to `/chat` — same route, same resolved mode, so
		// this component never unmounts and the lazy-connect effect never re-runs.
		// Navigating would leave the transport RESET (activeMode null, no welcome,
		// composer stuck on "Connecting...") with nothing scheduled to re-seed it.
		// Re-seed in place instead; the effect's cleanup does the disconnect.
		if (!canReturnToPicker) {
			setSessionEpoch((n) => n + 1);
			return;
		}

		disconnect();
		// Carry the district context back to the picker (dropping `mode`, which is
		// what the student is going back to re-choose). Without `d`/`features` the
		// picker can't tell which surfaces this district has and would bounce the
		// student straight into /chat again.
		navigate({
			to: '/',
			search: {
				features: urlFeaturesRaw,
				d: districtParam,
				embed,
			},
		});
	}, [
		canReturnToPicker,
		disconnect,
		navigate,
		sendSessionEnded,
		urlFeaturesRaw,
		districtParam,
		embed,
	]);

	// Confirmed from the "End this chat?" dialog. The dialog stays open, buttons
	// disabled, until Connect acknowledges: a double tap can't send a second
	// disconnect, and the student isn't shown "Chat ended" for a contact the
	// counselor still sees as live. A failure is reported inside the dialog —
	// not through `error`, which would escalate to the crisis card.
	const handleConfirmEndChat = useCallback(
		async (event: React.MouseEvent) => {
			event.preventDefault();
			setIsEndingChat(true);
			setEndChatFailed(false);
			const ended = await endChat();
			setIsEndingChat(false);
			if (!ended) {
				setEndChatFailed(true);
				return;
			}
			setIsEndChatDialogOpen(false);
			sendSessionEnded('user-closed');
		},
		[endChat, sendSessionEnded],
	);

	const handleFilesAdded = useCallback(
		(files: File[]) => {
			for (const file of files) {
				sendAttachment(file);
			}
		},
		[sendAttachment],
	);

	const isReachoutAI = activeMode === 'reachout-ai';
	const isClassicChat = activeMode === 'classic-chat';
	const isConnecting = status === 'connecting';
	const isConnected = status === 'connected';
	const isEnded = status === 'ended';
	const isLazyReady = (isReachoutAI || isClassicChat) && status === 'disconnected';
	const isWithCounselor = handoverStatus === 'initiated' || handoverStatus === 'connected';
	const isHandoverConnected = isReachoutAI && handoverStatus === 'connected';
	// A human is on the other end: either the AI session handed over, or this is
	// the classic transport, which only ever talks to Connect.
	const isCounselorActive = isHandoverConnected || (isClassicChat && isConnected);
	const canSend = isConnected || isLazyReady;
	// Available before the session exists too: sendAttachment lazily connects,
	// and a student whose opening move is "here's the message that scared me"
	// shouldn't have to send a text first to reveal the paperclip.
	const canAttach = isClassicChat && (isConnected || isLazyReady);
	// Only a live Connect contact can be ended. Before the first message
	// (lazy-ready) no contact exists; while reconnecting the chat session is
	// still held and `disconnectParticipant` reaches Connect over HTTPS.
	// AI-to-counselor handovers have no student-side Connect session, but the
	// AppSync transport's endChat publishes `end-chat` and the backend tells
	// reachout-contact-center to disconnect the contact. That covers both a
	// handover still waiting for a counselor and one a counselor has joined.
	const isSessionLive = isConnected || status === 'reconnecting';
	const canEndChat = isSessionLive && (isClassicChat || (isReachoutAI && isWithCounselor));

	/**
	 * Screenshots are the common case here — a student explaining a message
	 * they received, or a form they're stuck on, has it on the clipboard rather
	 * than saved to disk. filesFromClipboard ignores pastes that also carry
	 * text, so ordinary copy-paste of words still lands in the textarea.
	 */
	const handleComposerPaste = useCallback(
		(event: React.ClipboardEvent<HTMLTextAreaElement>) => {
			if (!canAttach) return;
			const files = filesFromClipboard(event.clipboardData);
			if (files.length === 0) return;

			event.preventDefault();
			handleFilesAdded(files);
		},
		[canAttach, handleFilesAdded],
	);

	// The student's turn is "outstanding" when the last thing anyone *said* came
	// from them. Trailing systemEvent entries (counselor joined/left markers,
	// etc.) are timeline furniture, not replies — skipping them keeps the
	// indicator up instead of blinking off when an event lands mid-wait.
	const lastSpeakerRole = useMemo(() => {
		for (let i = messages.length - 1; i >= 0; i--) {
			const m = messages[i];
			if (!m.systemEvent) return m.role;
		}
		return null;
	}, [messages]);

	const isWaitingForResponse =
		isReachoutAI &&
		!isWithCounselor &&
		lastSpeakerRole === 'user' &&
		!streamingContent &&
		!isRemoteTyping;

	// The in-flight reply, shaped as a message so the same bubble renders it
	// before and after FINALIZE_STREAMING. The id is the streaming id when we
	// have one so React keeps the node across the swap instead of remounting.
	const streamingMessage = useMemo<ChatMessage | null>(() => {
		if (!streamingContent) return null;
		return {
			id: streamingMessageId ?? 'streaming',
			role: 'assistant',
			content: streamingContent,
			timestamp: new Date().toISOString(),
			isStreaming: true,
		};
	}, [streamingContent, streamingMessageId]);

	// A counselor who hasn't started typing yet emits nothing — Connect's
	// `.typing` event (relayed as connect-typing) only fires once they're
	// actually at the keyboard. Between the student's message and that first
	// keystroke the transcript would sit silent, which reads as "nobody is
	// there", so it says it is waiting. It must not show typing dots: the wait
	// covers the contact flow's automated prompts and the whole queue, where no
	// counselor has even been assigned, and the dots read as "someone is typing".
	const isWaitingForCounselor = isCounselorActive && lastSpeakerRole === 'user' && !isRemoteTyping;
	const showWaitingForCounselor = isWaitingForCounselor && !isEnded;

	// Typing dots, two sources: a real typing event from the other side, or an
	// outstanding turn we're waiting on the bot for (it never emits typing
	// events, and always answers). `streamingContent` means tokens are already
	// arriving, so the reply itself is the progress signal.
	const showTypingIndicator =
		(isRemoteTyping || isWaitingForResponse) && !isEnded && !streamingContent;

	// A newly deployed build may reload the page only while that loses nothing:
	// no message sent (handoff history counts) and nothing typed in the composer.
	const hasStudentWritten = input.trim() !== '' || messages.some((m) => m.role === 'user');
	useEffect(() => {
		setConversationInProgress(hasStudentWritten);
	}, [hasStudentWritten]);

	// --- Safety fallback ladder -------------------------------------------
	// Rung 1: AI chat (AppSync). Rung 2: in-session counselor handover.
	// Rung 3: direct Amazon Connect via the classic transport (independent
	// infrastructure). Rung 4: static crisis card from the precached shell.
	//
	// A message that never left the device counts as a failure too. The bubble
	// itself carries no "Not sent — try again" footer any more: whatever broke,
	// the student reads one calm card with the 988 number, never a red retry
	// link under their own words.
	const anyMessageFailed = messages.some((msg) => msg.role === 'user' && msg.error === true);
	const anyFailure = !!error || anyMessageFailed || handoverStatus === 'failed';
	const showDirectConnectOffer = isReachoutAI && anyFailure && canSwitchToCounselor;
	const showCrisisCard = anyFailure && !showDirectConnectOffer;

	// One message per failure, and always the same one. The raw transport
	// strings (CHAT_ERRORS, CLASSIC_CHAT_CONNECTION_ERROR) still land in `error`
	// for tests and the console, but a student sees the card's own copy.
	const crisisHeadline = showCrisisCard ? CRISIS_CARD_HEADLINE : undefined;

	// District contact info for the crisis card: live probe result first, then
	// the sessionStorage copy from a previous successful probe (works offline).
	const chatInfoText = useMemo(() => {
		if (endpointStatus.state === 'enabled' && endpointStatus.chatInfoText) {
			return endpointStatus.chatInfoText;
		}
		if (!districtId) return undefined;
		try {
			return sessionStorage.getItem(chatInfoStorageKey(districtId)) ?? undefined;
		} catch {
			return undefined;
		}
	}, [endpointStatus, districtId]);

	// The counselor bundle is 578 KB and no longer blocks first paint, so warm it
	// as soon as that path looks likely — on the classic transport itself, and on
	// the rung-3 offer, which is one tap away from needing it.
	useEffect(() => {
		if (isClassicChat || showDirectConnectOffer) prefetchConnectChatJS();
	}, [isClassicChat, showDirectConnectOffer]);

	// --- Ambient embed -----------------------------------------------------
	// Hold the gates (CAPTCHA, invite) until the student interacts: an ambient
	// embed lives on a school homepage, and auto-showing a CAPTCHA to every
	// visitor defeats the point. The lazy-connect effect also waits on the
	// gates, so nothing can be sent until they clear.
	const ambientHoldGates = isAmbient && !hasInteracted;

	const ambientGreetingText =
		districtGreeting ?? (mode === 'reachout-ai' ? DEFAULT_AI_GREETING : DEFAULT_COUNSELOR_WELCOME);

	const startAmbientChat = useCallback(
		(text: string) => {
			setHasInteracted(true);
			sendMessage(text);
			sendMessageSent();
		},
		[sendMessage, sendMessageSent],
	);

	const ambientChips = useMemo<AmbientChip[]>(() => {
		if (
			(!isAmbient && embedContext.source === 'element') ||
			hasInteracted ||
			mode !== 'reachout-ai' ||
			gated ||
			messages.some((message) => message.role === 'user')
		)
			return [];
		const chips: AmbientChip[] = [
			{
				label: 'I’m not sure where to start',
				onSelect: () => startAmbientChat('I’m not sure where to start'),
			},
			{
				label: 'I want to talk',
				onSelect: () => startAmbientChat('I want to talk'),
			},
			{
				label: 'I’m feeling stressed',
				onSelect: () => startAmbientChat('I’m feeling stressed'),
			},
		];
		if (canChooseCounselor) {
			chips.push({
				label: 'Talk to a counselor',
				onSelect: () => {
					setHasInteracted(true);
					switchToDirectConnect();
				},
			});
		}
		return chips;
	}, [
		isAmbient,
		embedContext.source,
		hasInteracted,
		mode,
		gated,
		messages,
		canChooseCounselor,
		startAmbientChat,
		switchToDirectConnect,
	]);

	const headerTitle = isCounselorActive ? 'Counselor' : isReachoutAI ? 'Nomi' : 'Counselor';

	// In-page only: a school's layout can squeeze the element into a sidebar,
	// so offer the same district and surface on the chat origin in its own tab.
	const fullPageUrl = fullPageUrlFor(embedContext, { mode, features: urlFeaturesRaw });
	const headerAvatar = isCounselorActive
		? resolveAssetUrl('/counselor.png')
		: isReachoutAI
			? resolveAssetUrl('/nomi.png')
			: resolveAssetUrl('/counselor.png');
	const headerFallback = isCounselorActive ? 'C' : isReachoutAI ? 'N' : 'C';

	const headerStatus = (() => {
		if (isConnecting) return isReachoutAI ? 'Connecting...' : 'Connecting to counselor...';
		if (handoverStatus === 'initiated') return 'Connecting to a counselor...';
		if (isRemoteTyping) return `${remoteTypingName ?? headerTitle} is typing...`;
		// The bot never emits typing events — an outstanding student turn is the
		// only signal that a reply is on its way.
		if (isWaitingForResponse) return `${headerTitle} is typing...`;
		// Deliberately not "is typing": nothing has told us the counselor started.
		if (isWaitingForCounselor) return 'Waiting for a counselor...';
		if (isConnected) return 'Online';
		if (isEnded) return 'Chat ended';
		if (status === 'reconnecting') return 'Reconnecting...';
		if (isLazyReady) return 'Ready';
		if (status === 'disconnected') return 'Disconnected';
		return '';
	})();

	if (isDistrictDisabled || isAccessBlocked) {
		return (
			<div
				{...testId(isOriginBlocked ? STATE.originBlocked : STATE.disabled)}
				className="flex flex-1 flex-col items-center overflow-y-auto px-6 py-6 text-center"
			>
				<div className="m-auto w-full">
					<div className="mx-auto max-w-sm">
						{isOriginBlocked ? (
							<>
								<h1 className="font-semibold text-lg mb-2">Chat is unavailable from this site</h1>
								<p className="text-sm text-muted-foreground">
									This chat only runs on your school's own website.
								</p>
							</>
						) : (
							<>
								<h1 className="font-semibold text-lg mb-2">Chat is temporarily unavailable</h1>
								<p className="text-sm text-muted-foreground">Please try again later.</p>
							</>
						)}
					</div>
					<div className="mx-auto mt-6 w-full max-w-md text-left">
						<CrisisResources chatInfoText={chatInfoText} />
					</div>
				</div>
			</div>
		);
	}

	if (botChallenge.state === 'pending' && !ambientHoldGates) {
		return (
			<div
				{...testId(STATE.botChallenge)}
				className="flex flex-1 flex-col items-center justify-center px-6 text-center"
			>
				<div className="max-w-sm">
					<h1 className="font-semibold text-lg mb-3">Quick check</h1>
					<p className="text-sm text-muted-foreground mb-4">
						One quick safety check before we start chatting.
					</p>
					<div ref={botChallenge.container} className="flex justify-center" />
				</div>
			</div>
		);
	}

	if (botChallenge.state === 'failed' && !ambientHoldGates) {
		return (
			<div
				{...testId(STATE.botChallengeFailed)}
				className="flex flex-1 flex-col items-center overflow-y-auto px-6 py-6 text-center"
			>
				<div className="m-auto w-full">
					<div className="mx-auto max-w-sm">
						<h1 className="font-semibold text-lg mb-2">Couldn&apos;t start chat</h1>
						{/* `botChallenge.reason` is raw AWS WAF SDK text — "WAF SDK script
                failed to load from https://…jsapi.js", which a school content
                filter can trigger. It goes to the console, not to a student.
                The numbers below are what actually matters here. */}
						<p className="text-sm text-muted-foreground mb-4">
							We couldn&apos;t load the safety check that has to run before chat starts. Refreshing
							the page usually fixes it.
						</p>
						<button
							type="button"
							// An in-page mount supplies onReload (re-mount just the chat);
							// reloading would throw away the school's whole page.
							onClick={() => (embedContext.onReload ?? (() => window.location.reload()))()}
							className="text-sm text-primary underline underline-offset-2"
						>
							Try again
						</button>
					</div>
					<div className="mx-auto mt-6 w-full max-w-md text-left">
						<CrisisResources chatInfoText={chatInfoText} />
					</div>
				</div>
			</div>
		);
	}

	if (inviteGateEnabled && !invite.unlocked && !ambientHoldGates) {
		return (
			<div
				{...testId(STATE.inviteGate)}
				className="flex flex-1 flex-col items-center justify-center px-6"
			>
				<InviteGate
					isVerifying={invite.isVerifying}
					error={invite.error}
					onSubmit={invite.submit}
				/>
				{/* A student who cannot get past this gate — wrong code, or no code at
            all — was previously offered nothing. The gate is exactly where
            someone in trouble gets stuck. */}
				<div className="mt-6 w-full max-w-md">
					<CrisisResources chatInfoText={chatInfoText} />
				</div>
			</div>
		);
	}

	return (
		<div {...testId(CHAT.root)} className="flex flex-1 flex-col min-h-0">
			<header
				className={cn(
					'flex items-center gap-3 border-b px-4 py-3 pt-[max(.75rem,env(safe-area-inset-top))] shrink-0',
					// The embed panel draws its own close button over the top-right
					// corner of this iframe (it lives on the host page — nothing inside
					// the frame can dismiss the panel). Reserve the space so it doesn't
					// sit on top of the open-in-tab link.
					embedded && 'pe-14',
				)}
			>
				{canReturnToPicker && (
					<Button
						variant="ghost"
						size="icon"
						onClick={handleBack}
						className="shrink-0"
						aria-label="Back to chat selection"
						{...testId(CHAT.back)}
					>
						<ArrowLeft className="h-5 w-5" />
					</Button>
				)}
				<Avatar className="h-9 w-9 border">
					<AvatarImage src={headerAvatar} alt={headerTitle} />
					<AvatarFallback>{headerFallback}</AvatarFallback>
				</Avatar>
				<div className="min-w-0 flex-1">
					<p {...testId(CHAT.headerTitle)} className="font-semibold text-sm">
						{headerTitle}
					</p>
					<p {...testId(CHAT.headerStatus)} className="text-xs text-muted-foreground">
						{headerStatus}
					</p>
				</div>

				{canEndChat && (
					<Button
						variant="ghost"
						size="sm"
						onClick={() => {
							setEndChatFailed(false);
							setIsEndChatDialogOpen(true);
						}}
						className="shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
						{...testId(CHAT.endChat)}
					>
						<LogOut aria-hidden />
						End chat
					</Button>
				)}

				{fullPageUrl && (
					<a
						href={fullPageUrl}
						target="_blank"
						rel="noopener noreferrer"
						{...testId(CHAT.openFullPage)}
						aria-label="Open the chat in its own tab"
						title="Opens a new conversation in its own tab"
						className={cn(
							'shrink-0 inline-flex size-11 items-center justify-center rounded-lg',
							'text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground',
							'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
						)}
					>
						<ExternalLink className="size-4" aria-hidden />
					</a>
				)}
			</header>

			<AlertDialog
				open={isEndChatDialogOpen}
				onOpenChange={(open) => {
					if (!isEndingChat) setIsEndChatDialogOpen(open);
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>End this chat?</AlertDialogTitle>
						<AlertDialogDescription>
							Your counselor will be told you've left, and you won't be able to send more messages
							in this conversation.
						</AlertDialogDescription>
					</AlertDialogHeader>
					{endChatFailed && (
						<p role="alert" className="text-sm text-destructive">
							We couldn't end the chat just now. It's still open — try again in a moment.
						</p>
					)}
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isEndingChat} {...testId(CHAT.endChatCancel)}>
							Keep chatting
						</AlertDialogCancel>
						<AlertDialogAction
							disabled={isEndingChat}
							onClick={handleConfirmEndChat}
							className={buttonVariants({ variant: 'destructive' })}
							{...testId(CHAT.endChatConfirm)}
						>
							{isEndingChat ? 'Ending…' : 'End chat'}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<MessageScrollerProvider autoScroll>
				<MessageScroller className="flex-1 min-h-0">
					<MessageScrollerViewport>
						<MessageScrollerContent {...testId(CHAT.messages)} className="gap-3 px-4 py-4">
							{isReachoutAI && !isCounselorActive && (
								<Marker {...testId(CHAT.aiDisclosure)} className="justify-center">
									<MarkerContent className="text-center text-xs">
										Nomi is an AI, not a person.
										{canChooseCounselor && ' You can also ask to talk to a counselor.'}
									</MarkerContent>
								</Marker>
							)}

							{isAmbient && <AmbientGreeting text={ambientGreetingText} chips={ambientChips} />}

							{isConnecting && isClassicChat && (
								<div className="flex items-center justify-center py-12">
									<Loader variant="text-shimmer" text="Connecting to counselor..." />
								</div>
							)}

							{showDirectConnectOffer && (
								<div
									{...testId(STATE.directConnectOffer)}
									role="alert"
									className="mx-auto w-full max-w-md rounded-xl border bg-primary/5 p-4 text-center text-sm"
								>
									<p className="mb-3">
										I&apos;m having trouble with this chat. You can connect directly with a
										counselor instead.
									</p>
									<Button onClick={switchToDirectConnect} className="gap-2">
										<Phone className="size-4" />
										Connect me to a counselor
									</Button>
								</div>
							)}

							{messages.map((msg) => (
								// MessageScrollerItem carries content-visibility:auto, so a
								// long transcript stops paying layout+paint for the bubbles
								// that are scrolled out of view.
								<MessageScrollerItem key={msg.id}>
									{msg.systemEvent ? (
										// Timeline markers from the backend event stream —
										// counselor joined/left during a handover, etc. Previously
										// these were filtered out and the header status was the
										// only signal.
										msg.content ? (
											<Marker variant="separator">
												<MarkerContent>{msg.content}</MarkerContent>
											</Marker>
										) : null
									) : (
										<MessageBubble
											msg={msg}
											isReachoutAI={isReachoutAI}
											onDownloadAttachment={getAttachmentUrl}
										/>
									)}
								</MessageScrollerItem>
							))}

							{/* Below the transcript, not above it: a send that failed
                  mid-conversation leaves the student looking at their own dimmed
                  bubble, and the explanation belongs right under it. */}
							{showCrisisCard && (
								<CrisisResources headline={crisisHeadline} chatInfoText={chatInfoText} />
							)}

							{/* The reply as it arrives. Without this the tokens accumulate
                  into `streamingContent` unseen, the typing dots switch off at
                  the first one (both indicators end in `!streamingContent`),
                  and the student watches an empty screen until the whole
                  message lands. Rendered through MessageBubble so finalizing
                  swaps nothing visually. */}
							{streamingMessage && (
								<MessageScrollerItem {...testId(CHAT.streaming)}>
									<MessageBubble
										msg={streamingMessage}
										isReachoutAI={isReachoutAI}
										onDownloadAttachment={getAttachmentUrl}
									/>
								</MessageScrollerItem>
							)}

							{showTypingIndicator && (
								<Message {...testId(CHAT.typing)} align="start">
									<MessageContent>
										{/* Same shell as an incoming message so the dots read as
                        "a reply is being written here", not as a page spinner. */}
										<Bubble variant="secondary" className="max-w-[85%]">
											<BubbleContent
												aria-live="polite"
												// Mirrors headerStatus so a screen reader hears the
												// same claim the sighted header makes.
												aria-label={headerStatus}
											>
												<Loader variant="typing" size="sm" />
											</BubbleContent>
										</Bubble>
									</MessageContent>
								</Message>
							)}

							{showWaitingForCounselor && (
								<Marker
									{...testId(CHAT.waitingForCounselor)}
									role="status"
									className="justify-center"
								>
									<MarkerContent className="text-center text-xs">
										Waiting for a counselor…
									</MarkerContent>
								</Marker>
							)}

							{isEnded && (
								<Marker {...testId(STATE.ended)} variant="separator" className="py-4">
									<MarkerContent>
										This chat has ended.{' '}
										<button
											type="button"
											onClick={handleBack}
											className="text-primary underline underline-offset-2 hover:no-underline"
										>
											Start a new conversation
										</button>
									</MarkerContent>
								</Marker>
							)}
							{!isAmbient && <ConversationStarters chips={ambientChips} />}
						</MessageScrollerContent>
					</MessageScrollerViewport>

					<MessageScrollerButton />
				</MessageScroller>
			</MessageScrollerProvider>

			{/* Hidden during the ambient pre-interaction phase — the "Talk to a
          counselor" quick-reply chip covers it until the student engages. */}
			{isReachoutAI && canChooseCounselor && !isEnded && !isWithCounselor && !ambientHoldGates && (
				<div className="shrink-0 px-4 pt-2">
					<button
						type="button"
						onClick={requestHumanAssistance}
						{...testId(CHAT.talkToCounselor)}
						className={cn(
							'flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5',
							'text-sm font-medium text-primary',
							'bg-primary/10',
							'transition-all duration-150',
							'hover:bg-primary/20 hover:shadow-sm',
							'active:bg-primary/25 active:scale-[0.98]',
							'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
						)}
					>
						<Phone className="size-4" />
						Talk to a Counselor
					</button>
				</div>
			)}

			{attachmentError && (
				<div className="shrink-0 px-4 pt-2">
					<div
						role="status"
						className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2 text-destructive text-xs"
					>
						<p className="flex-1">{attachmentError}</p>
						<button
							type="button"
							onClick={dismissAttachmentError}
							className="shrink-0 font-medium underline underline-offset-2 hover:no-underline"
						>
							Dismiss
						</button>
					</div>
				</div>
			)}

			{/* index.html opts into viewport-fit=cover, so on a notched iPhone the
          composer sat under the home indicator. pb-4 stays as the floor for
          every other device. */}
			<div className="shrink-0 px-4 pt-2 pb-[max(1rem,env(safe-area-inset-bottom))]">
				<FileUpload
					onFilesAdded={handleFilesAdded}
					accept={ATTACHMENT_ACCEPT}
					disabled={!canAttach}
				>
					<FileUploadContent>
						<div className="rounded-2xl border-2 border-dashed border-primary/60 bg-background px-8 py-6 text-center shadow-lg">
							<Paperclip className="mx-auto mb-2 size-6 text-primary" />
							<p className="font-medium text-sm">Drop to send</p>
							<p className="mt-1 text-xs text-muted-foreground">Images and documents up to 20MB</p>
						</div>
					</FileUploadContent>

					<PromptInput
						value={input}
						onValueChange={setInput}
						onSubmit={handleSend}
						isLoading={isConnecting}
						// During the ambient pre-interaction phase the input must stay
						// focusable — focusing it is what counts as "interacting" and
						// reveals any held gate. handleSend still refuses until a
						// transport mode is actually ready.
						disabled={(!canSend && !ambientHoldGates) || isEnded}
					>
						<PromptInputTextarea
							ref={composerRef}
							aria-label={`Message ${headerTitle}`}
							{...testId(CHAT.composer)}
							placeholder={
								isEnded
									? 'Chat ended'
									: canSend || ambientHoldGates
										? `Message ${headerTitle}...`
										: 'Connecting...'
							}
							onFocus={() => {
								if (isAmbient) setHasInteracted(true);
							}}
							onPaste={handleComposerPaste}
							onKeyDown={() => {
								if (isConnected && isClassicChat) {
									sendTyping();
								}
							}}
						/>
						<PromptInputActions className="justify-end gap-1">
							{canAttach && (
								<FileUploadTrigger
									className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-accent-foreground"
									aria-label="Attach file"
									{...testId(CHAT.attach)}
								>
									<Paperclip className="h-4 w-4" />
								</FileUploadTrigger>
							)}
							<PromptInputAction tooltip="Send message">
								<Button
									variant="default"
									size="icon"
									// The tooltip is not an accessible name and never fires on
									// touch, which is most of this audience — so the button was
									// announced as "button" and had no label under a long-press.
									aria-label="Send message"
									className="h-8 w-8 rounded-full"
									disabled={!input.trim() || !canSend || isEnded}
									onClick={handleSend}
									{...testId(CHAT.send)}
								>
									<Send className="h-4 w-4" />
								</Button>
							</PromptInputAction>
						</PromptInputActions>
					</PromptInput>
				</FileUpload>
			</div>
		</div>
	);
}
